import type { LearningExercise } from "../types";

// EQE-Lernmodul: EPÜ Fristen/Rechtsverlust + Schutzbereich (Sprints E2 + E3)
// Themen: Art. 121/R. 135 Weiterbehandlung, Art. 122/R. 136 Wiedereinsetzung,
//         R. 131/132/134 Fristberechnung, Art. 86/R. 51 Jahresgebühren,
//         Art. 63/64/67 Laufzeit und Wirkung, Art. 69 + Auslegungsprotokoll.
// pathId: "epu-eqe", mode: "ausbildung", reviewStatus: "draft"
// Quellen-Basis: 🟢 EPÜ (epo.org/law-practice, 2020-Fassung)
// ID-Bereich: eqe-frist-001 … eqe-frist-040
export const eqeFristenSchutzbereich: LearningExercise[] = [

  // ─── MC — Art. 121 / R. 135 Weiterbehandlung ────────────────────────────

  {
    id: "eqe-frist-001",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Fristen — Art. 121 Weiterbehandlung",
    title: "Weiterbehandlung: Anwendungsbereich",
    prompt:
      "Ein Anmelder versäumt eine Frist gegenüber dem EPA. Unter welcher Voraussetzung steht ihm die Weiterbehandlung nach Art. 121 EPÜ grundsätzlich offen?",
    options: [
      "Die versäumte Frist muss eine vom EPA gesetzte Frist sein, die auf einen Verlust von Rechten hinweist.",
      "Der Anmelder muss nachweisen, dass er alle gebotene Sorgfalt beachtet hat.",
      "Die versäumte Handlung muss innerhalb von zwei Monaten nach dem Hinweis auf den Rechtsverlust nachgeholt werden, sofern die Frist nicht nach R. 135(2) EPÜ ausgeschlossen ist.",
      "Die Weiterbehandlung ist nur bei Fristen nach dem PCT zulässig.",
    ],
    answers: [2],
    explanation:
      "Art. 121 Abs. 1 EPÜ: Versäumt ein Anmelder eine Frist gegenüber dem EPA, kann er die Weiterbehandlung beantragen. Der Antrag ist innerhalb von zwei Monaten nach dem Hinweis auf den Rechtsverlust zu stellen; die versäumte Handlung ist gleichzeitig nachzuholen und die Weiterbehandlungsgebühr zu zahlen (R. 135 Abs. 1 EPÜ). Ein Sorgfaltsnachweis ist nicht erforderlich — das unterscheidet die Weiterbehandlung von der Wiedereinsetzung (Art. 122 EPÜ). Bestimmte Fristen sind nach R. 135 Abs. 2 EPÜ ausgeschlossen.",
    sourceIds: ["art121epc", "r135epc"],
    sources: [
      { id: "art121epc", label: "Art. 121 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a121.html" },
      { id: "r135epc", label: "R. 135 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r135.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["eqe", "fristen", "weiterbehandlung", "art121"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-002",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Fristen — Art. 121 Weiterbehandlung",
    title: "Von Weiterbehandlung ausgeschlossene Fristen",
    prompt:
      "Welche der folgenden Fristen ist nach R. 135(2) EPÜ von der Weiterbehandlung nach Art. 121 EPÜ ausgeschlossen?",
    options: [
      "Frist zur Einreichung der Erwiderung auf einen Prüfungsbescheid nach Art. 94 EPÜ.",
      "Frist zur Zahlung der Anmeldegebühr nach R. 38 EPÜ.",
      "Frist zur Stellung des Prüfungsantrags nach Art. 94 Abs. 2 EPÜ.",
      "Prioritätsfrist nach Art. 87 Abs. 1 EPÜ (12 Monate).",
    ],
    answers: [3],
    explanation:
      "R. 135 Abs. 2 EPÜ schließt bestimmte Fristen von der Weiterbehandlung aus. Die Prioritätsfrist des Art. 87 Abs. 1 EPÜ (12 Monate) kann nicht durch Weiterbehandlung verlängert werden. Ebenso ausgeschlossen sind u.a. die Frist für den Einspruch (Art. 99 EPÜ), die Wiedereinsetzungsfrist selbst und die Frist zum Einreichen einer Beschwerdebegründung. Fristen für Erwiderungen auf Bescheide, Anmeldegebühren und Prüfungsanträge sind hingegen grundsätzlich weiterbehandlungsfähig.",
    sourceIds: ["art121epc", "r135epc"],
    sources: [
      { id: "art121epc", label: "Art. 121 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a121.html" },
      { id: "r135epc", label: "R. 135 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r135.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Ausnahme",
    skillTags: ["eqe", "fristen", "weiterbehandlung", "art121"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-003",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Fristen — Art. 121 Weiterbehandlung",
    title: "Weiterbehandlung: Frist und Nachholung (Fallkonstellation)",
    prompt:
      "Anmelderin A erhält am 3. März einen Bescheid, aus dem hervorgeht, dass sie durch Versäumen der Erwiderungsfrist ein Recht verloren hat. Wann endet die Frist zur Beantragung der Weiterbehandlung?",
    options: [
      "3. April desselben Jahres (1 Monat).",
      "3. Mai desselben Jahres (2 Monate).",
      "3. September desselben Jahres (6 Monate).",
      "3. März des Folgejahres (12 Monate).",
    ],
    answers: [1],
    explanation:
      "R. 135 Abs. 1 EPÜ: Der Antrag auf Weiterbehandlung muss innerhalb von zwei Monaten nach dem Hinweis auf den Rechtsverlust gestellt werden. Der Hinweis erging am 3. März; zwei Monate danach ist der 3. Mai. Gleichzeitig müssen die versäumte Handlung nachgeholt und die Weiterbehandlungsgebühr gezahlt werden. Eine Verlängerung um weitere Monate ist nicht vorgesehen.",
    sourceIds: ["art121epc", "r135epc"],
    sources: [
      { id: "art121epc", label: "Art. 121 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a121.html" },
      { id: "r135epc", label: "R. 135 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r135.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["eqe", "fristen", "weiterbehandlung", "art121"],
    reviewStatus: "beta-ready",
  },

  // ─── MC — Art. 122 / R. 136 Wiedereinsetzung ────────────────────────────

  {
    id: "eqe-frist-004",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Fristen — Art. 122 Wiedereinsetzung",
    title: "Wiedereinsetzung: Kernvoraussetzung",
    prompt:
      "Welche materielle Hauptvoraussetzung muss der Antragsteller für eine erfolgreiche Wiedereinsetzung nach Art. 122 EPÜ darlegen?",
    options: [
      "Er muss nachweisen, dass das EPA einen Fehler begangen hat.",
      "Er muss darlegen, dass er trotz Beachtung aller gebotenen Sorgfalt an der Einhaltung der Frist gehindert war.",
      "Er muss eine eidesstattliche Versicherung der verantwortlichen Person vorlegen.",
      "Es genügt, dass die versäumte Handlung innerhalb von 12 Monaten nachgeholt wird.",
    ],
    answers: [1],
    explanation:
      "Art. 122 Abs. 1 EPÜ: Der Anmelder oder Patentinhaber, der trotz Beachtung aller gebotenen Sorgfalt verhindert war, eine Frist einzuhalten, wird auf Antrag in den vorigen Stand eingesetzt. Der Maßstab 'all due care' (alle gebotene Sorgfalt) ist der zentrale Prüfungspunkt; er betrifft sowohl den Antragsteller selbst als auch etwaige Vertreter und interne Überwachungssysteme. Ein EPA-Fehler oder eine eidesstattliche Versicherung sind keine Tatbestandsmerkmale; die bloße Nachholung innerhalb von 12 Monaten reicht allein nicht aus.",
    sourceIds: ["art122epc", "r136epc"],
    sources: [
      { id: "art122epc", label: "Art. 122 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a122.html" },
      { id: "r136epc", label: "R. 136 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r136.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "fristen", "wiedereinsetzung", "art122"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-005",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Fristen — Art. 122 Wiedereinsetzung",
    title: "Wiedereinsetzung: Antragsfrist (Fallkonstellation)",
    prompt:
      "Patentanwältin P stellt am 15. Juli fest, dass sie die Frist zur Einreichung der Beschwerdebegründung am 20. Juni versäumt hat. Bis wann muss sie den Wiedereinsetzungsantrag stellen?",
    options: [
      "Bis zum 15. August (1 Monat nach Kenntnisnahme).",
      "Bis zum 15. September (2 Monate nach Kenntnisnahme) und spätestens bis zum 20. Juni des Folgejahres (12-Monats-Höchstfrist ab dem Tag, an dem die Frist ablief).",
      "Bis zum 20. Dezember (6 Monate ab Fristablauf).",
      "Es gibt keine Frist — der Antrag kann jederzeit gestellt werden.",
    ],
    answers: [1],
    explanation:
      "R. 136 Abs. 1 EPÜ: Der Antrag auf Wiedereinsetzung ist innerhalb von zwei Monaten nach Wegfall des Hindernisses zu stellen. Das Hindernis fiel am 15. Juli weg (Tag der Kenntnisnahme), sodass die 2-Monats-Frist am 15. September endet. Daneben gilt eine absolute 12-Monats-Höchstfrist: der Antrag muss in jedem Fall innerhalb von 12 Monaten nach Ablauf der versäumten Frist (20. Juni) eingereicht werden. Beide Fristen müssen kumulativ eingehalten werden; die zuerst ablaufende ist maßgeblich.",
    sourceIds: ["art122epc", "r136epc"],
    sources: [
      { id: "art122epc", label: "Art. 122 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a122.html" },
      { id: "r136epc", label: "R. 136 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r136.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["eqe", "fristen", "wiedereinsetzung", "art122"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-006",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Fristen — Art. 122 Wiedereinsetzung",
    title: "Von Wiedereinsetzung ausgeschlossene Fristen",
    prompt:
      "Welche der folgenden Fristen ist nach R. 136(3) EPÜ ausdrücklich von der Wiedereinsetzung ausgeschlossen?",
    options: [
      "Die Frist zur Erwiderung auf einen Prüfungsbescheid — für sie steht die Weiterbehandlung nach Art. 121 EPÜ offen, weshalb die Wiedereinsetzung nach R. 136(3) EPÜ ausgeschlossen ist.",
      "Die Prioritätsfrist nach Art. 87 Abs. 1 EPÜ.",
      "Die Frist zur Einreichung der Beschwerde nach Art. 108 S. 1 EPÜ.",
      "Die Frist zur Zahlung einer Jahresgebühr mit Zuschlag (R. 51 Abs. 2 EPÜ).",
    ],
    answers: [0],
    explanation:
      "R. 136 Abs. 3 EPÜ schließt die Wiedereinsetzung für alle Fristen aus, für die die Weiterbehandlung nach Art. 121 EPÜ zur Verfügung steht (sowie für die Wiedereinsetzungs-Antragsfrist selbst, R. 136 Abs. 1). Die Erwiderungsfrist auf einen Prüfungsbescheid ist weiterbehandlungsfähig — dort ist daher die Wiedereinsetzung ausgeschlossen (der Anmelder nutzt stattdessen die Weiterbehandlung). Die Prioritätsfrist (Art. 87 Abs. 1) ist dagegen NICHT ausgeschlossen: R. 136 Abs. 1 S. 2 EPÜ sieht hierfür eine eigene 2-Monats-Antragsfrist ab Ablauf der Prioritätsfrist vor (EPÜ 2000). Für die Beschwerdefrist (Art. 108) ist die Weiterbehandlung nach R. 135(2) ausgeschlossen, sodass dort gerade die Wiedereinsetzung in Betracht kommt.",
    sourceIds: ["art122epc", "r136epc"],
    sources: [
      { id: "art122epc", label: "Art. 122 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a122.html" },
      { id: "r136epc", label: "R. 136 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r136.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Ausnahme",
    skillTags: ["eqe", "fristen", "wiedereinsetzung", "art122"],
    // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-007",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Fristen — Weiterbehandlung vs. Wiedereinsetzung",
    title: "Abgrenzung Art. 121 / Art. 122 EPÜ (Fallkonstellation)",
    prompt:
      "Anmelder B hat die Frist zur Einreichung der Beschreibung nach Aufforderung versäumt. Der Hinweis auf den Rechtsverlust ist ihm bekannt. Welcher Rechtsbehelf ist vorrangig zu prüfen?",
    options: [
      "Sofort Wiedereinsetzung nach Art. 122 EPÜ mit Sorgfaltsnachweis.",
      "Weiterbehandlung nach Art. 121 EPÜ, sofern diese Frist nicht nach R. 135(2) ausgeschlossen ist — ohne Sorgfaltsnachweis.",
      "Beschwerde nach Art. 106 EPÜ gegen den Verlustbescheid.",
      "Wiedereinsetzung und Weiterbehandlung sind gleichrangig und können gleichzeitig beantragt werden.",
    ],
    answers: [1],
    explanation:
      "Art. 121 EPÜ (Weiterbehandlung) ist der einfachere und vorrangig zu prüfende Rechtsbehelf: Er erfordert keinen Sorgfaltsnachweis, sondern lediglich Antragstellung, Nachholung der Handlung und Gebührenzahlung innerhalb von zwei Monaten (R. 135 Abs. 1 EPÜ). Ist die Frist nicht nach R. 135 Abs. 2 EPÜ ausgeschlossen, greift Art. 121 EPÜ. Art. 122 EPÜ (Wiedereinsetzung) kommt subsidiär in Betracht, wenn Weiterbehandlung ausgeschlossen ist oder deren Frist ebenfalls verstrichen ist. Eine Beschwerde ist kein Mittel der Fristwiederherstellung.",
    sourceIds: ["art121epc", "art122epc"],
    sources: [
      { id: "art121epc", label: "Art. 121 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a121.html" },
      { id: "art122epc", label: "Art. 122 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a122.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["eqe", "fristen", "weiterbehandlung", "wiedereinsetzung", "art121", "art122"],
    reviewStatus: "beta-ready",
  },

  // ─── MC — R. 131 / R. 132 / R. 134 Fristberechnung ─────────────────────

  {
    id: "eqe-frist-008",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Fristen — R. 131 Fristberechnung",
    title: "Fristberechnung nach R. 131 EPÜ",
    prompt:
      "Eine Mitteilung des EPA wird dem Anmelder laut Fiktion zugestellt. Wann beginnt eine nach dieser Zustellung laufende Monatsfrist nach R. 131 EPÜ?",
    options: [
      "Mit dem Tag der tatsächlichen Absendung durch das EPA.",
      "Am Tag nach dem Tag der fiktiven Zustellung (d.h. der Fristlauf beginnt ab dem nächsten Kalendertag).",
      "Genau an dem Tag, der als Zustelltag gilt.",
      "Erst nach Ablauf der in R. 126 EPÜ vorgesehenen Postlauffiktion.",
    ],
    answers: [1],
    explanation:
      "R. 131 Abs. 2 EPÜ: Bei Fristen, die durch eine Mitteilung ausgelöst werden, beginnt der Lauf der Frist am Tag nach dem Zugang (bzw. dem fiktiven Zustelltag). Der Zustelltag selbst wird also nicht eingerechnet (dies a quo non computatur in termino). Die Frist endet dann am entsprechenden Tag des letzten Monats (R. 131 Abs. 4 EPÜ). Diese Berechnung gilt unabhängig davon, ob der tatsächliche Absendezeitpunkt früher lag.",
    sourceIds: ["r131epc"],
    sources: [
      { id: "r131epc", label: "R. 131 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r131.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["eqe", "fristen", "fristberechnung", "r131"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-009",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Fristen — R. 134 Verlängerung",
    title: "Fristende an EPA-Ausfalltag",
    prompt:
      "Eine Frist läuft am Donnerstag, dem 25. Dezember, ab. Das EPA ist an diesem Tag geschlossen. Bis wann kann die fristwahrende Handlung noch eingereicht werden?",
    options: [
      "Bis zum 24. Dezember (Tag vor dem Ausfalltag).",
      "Bis zum nächsten Arbeitstag, an dem das EPA geöffnet ist.",
      "Bis zum 27. Dezember — Ausfalltage verlängern die Frist nur um genau zwei Tage.",
      "Die Frist kann nicht verlängert werden, da es sich um einen gesetzlichen Feiertag handelt.",
    ],
    answers: [1],
    explanation:
      "R. 134 Abs. 1 EPÜ: Läuft eine Frist an einem Tag ab, an dem das EPA für die Entgegennahme von Schriftstücken nicht geöffnet ist, verlängert sie sich bis zum ersten darauf folgenden Arbeitstag, an dem das EPA geöffnet ist. Der 25. Dezember ist ein Ausfalltag; die Frist verschiebt sich auf den nächsten Öffnungstag (in der Regel 27. Dezember, sofern auch der 26. Dezember ein Ausfalltag ist). R. 134 Abs. 2 EPÜ gilt entsprechend für Posttage (Unterbrechung der Postzustellung).",
    sourceIds: ["r134epc"],
    sources: [
      { id: "r134epc", label: "R. 134 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r134.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["eqe", "fristen", "fristberechnung", "r134"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-010",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Fristen — R. 132 Vom EPA gesetzte Fristen",
    title: "Mindestdauer vom EPA gesetzter Fristen",
    prompt:
      "Das EPA setzt in einem Bescheid eine Frist. Was gilt nach R. 132 EPÜ für die Mindestdauer?",
    options: [
      "Mindestens 1 Monat.",
      "Mindestens 2 Monate (höchstens 4 Monate); nur in besonderen Fällen kann die Frist auf Antrag bis zu 6 Monate verlängert werden.",
      "Mindestens 4 Monate.",
      "Mindestens 6 Monate für alle Fristen nach der Recherche.",
    ],
    answers: [1],
    explanation:
      "R. 132 Abs. 2 EPÜ: Vom EPA gesetzte Fristen betragen mindestens zwei Monate und höchstens vier Monate; nur in besonderen Fällen kann die Frist auf Antrag bis zu sechs Monate verlängert werden. Diese Mindestgarantie sichert dem Anmelder ausreichend Zeit für eine sachgerechte Reaktion. Fristen unter zwei Monaten sind grundsätzlich unzulässig. Vertraglich vereinbarte oder gesetzlich fixierte Fristen (z.B. die 9-Monats-Einspruchsfrist) sind davon nicht betroffen.",
    sourceIds: ["r132epc"],
    sources: [
      { id: "r132epc", label: "R. 132 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r132.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "fristen", "fristberechnung", "r132"],
    // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
    reviewStatus: "beta-ready",
  },

  // ─── MC — Art. 86 / R. 51 Jahresgebühren ───────────────────────────────

  {
    id: "eqe-frist-011",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Fristen — Art. 86 / R. 51 Jahresgebühren",
    title: "Jahresgebühren: Nachfrist und Zuschlag",
    prompt:
      "Der Anmelder hat die Jahresgebühr für das dritte Jahr nicht rechtzeitig gezahlt. Was sind die Rechtsfolgen und Fristen gemäß Art. 86 und R. 51 EPÜ?",
    options: [
      "Die Anmeldung gilt sofort als zurückgenommen; eine Nachzahlung ist nicht möglich.",
      "Es besteht eine 6-monatige Nachfrist mit einem Verspätungszuschlag; nach Ablauf gilt die Anmeldung als zurückgenommen.",
      "Die Jahresgebühr kann innerhalb von 12 Monaten ohne Zuschlag nachgezahlt werden.",
      "Das EPA setzt eine individuelle Frist von 2 bis 4 Monaten für die Nachzahlung.",
    ],
    answers: [1],
    explanation:
      "Art. 86 Abs. 1 EPÜ i.V.m. R. 51 Abs. 2 EPÜ: Jahresgebühren können noch innerhalb von sechs Monaten nach Fälligkeit mit einem Verspätungszuschlag gezahlt werden. Wird auch diese Nachfrist versäumt, gilt die europäische Patentanmeldung als zurückgenommen (Art. 86 Abs. 3 EPÜ). Bei einem erteilten Patent erlischt es. Die 6-Monats-Nachfrist ist eine gesetzliche Frist und kann nicht durch das EPA verkürzt oder verlängert werden.",
    sourceIds: ["art86epc", "r51epc"],
    sources: [
      { id: "art86epc", label: "Art. 86 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a86.html" },
      { id: "r51epc", label: "R. 51 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r51.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["eqe", "fristen", "jahresgebuehren", "art86"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-012",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Fristen — Art. 86 / R. 51 Jahresgebühren",
    title: "Jahresgebühren: Fälligkeit und Fristwahrung (Fallkonstellation)",
    prompt:
      "Anmelder C hat die Jahresgebühr für das vierte Schutzjahr am 30. November des Vorjahres zu zahlen. Er zahlt erst am 28. Mai des laufenden Jahres mit Verspätungszuschlag. Ist die Zahlung noch fristwahrend?",
    options: [
      "Nein, weil die Nachfrist nur 3 Monate beträgt.",
      "Ja — die Nachfrist beträgt 6 Monate; bei Fälligkeit am 30. November endet sie am 30. Mai. Die Zahlung am 28. Mai ist noch fristwahrend.",
      "Nein, weil ein Verspätungszuschlag zwingend bereits vor der Zahlung gesondert beantragt werden muss.",
      "Ja, aber nur wenn das EPA die Nachfrist im Einzelfall gewährt hat.",
    ],
    answers: [1],
    explanation:
      "R. 51 Abs. 2 EPÜ: Die Nachfrist beträgt 6 Monate ab dem Tag, an dem die Jahresgebühr fällig war. Fälligkeit am 30. November ergibt eine Nachfrist bis zum 30. Mai (sechs Monate später; der entsprechende Tag des sechsten Folgemonats nach R. 131 Abs. 4 EPÜ). Die Zahlung am 28. Mai liegt innerhalb dieser Nachfrist. Der Verspätungszuschlag ist automatisch zusammen mit der Gebühr zu zahlen; ein gesonderter Antrag ist nicht erforderlich.",
    sourceIds: ["art86epc", "r51epc"],
    sources: [
      { id: "art86epc", label: "Art. 86 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a86.html" },
      { id: "r51epc", label: "R. 51 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r51.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["eqe", "fristen", "jahresgebuehren", "art86"],
    reviewStatus: "beta-ready",
  },

  // ─── MC — Art. 63 / Art. 64 / Art. 67 Laufzeit und Wirkung ─────────────

  {
    id: "eqe-frist-013",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Schutzbereich — Art. 63 Laufzeit",
    title: "Laufzeit des europäischen Patents",
    prompt: "Wie lange gilt ein europäisches Patent nach Art. 63 EPÜ, und ab welchem Tag beginnt diese Frist?",
    options: [
      "15 Jahre ab dem Tag der Bekanntmachung der Erteilung im Europäischen Patentblatt.",
      "20 Jahre ab dem Anmeldetag der europäischen Patentanmeldung.",
      "20 Jahre ab dem Prioritätstag der Stammanmeldung.",
      "25 Jahre ab dem Anmeldetag, verlängerbar durch SPC.",
    ],
    answers: [1],
    explanation:
      "Art. 63 Abs. 1 EPÜ: Die Laufzeit des europäischen Patents beträgt 20 Jahre, gerechnet ab dem Anmeldetag der europäischen Patentanmeldung. Maßgeblich ist der tatsächliche Anmeldetag, nicht der Prioritätstag. Für bestimmte Arzneimittel und Pflanzenschutzmittel kann die Schutzdauer durch ein ergänzendes Schutzzertifikat (SPC) nach der EU-SPC-Verordnung um bis zu fünf Jahre verlängert werden; dies ist jedoch nicht im EPÜ selbst geregelt.",
    sourceIds: ["art63epc"],
    sources: [
      { id: "art63epc", label: "Art. 63 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a63.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "schutzbereich", "laufzeit", "art63"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-014",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Schutzbereich — Art. 64 Rechte aus dem Patent",
    title: "Rechte aus dem europäischen Patent (Art. 64 EPÜ)",
    prompt:
      "Welche Aussage zu den Rechten aus dem europäischen Patent nach Art. 64 EPÜ ist zutreffend?",
    options: [
      "Das europäische Patent verleiht einheitliche Rechte in allen 38 EPÜ-Vertragsstaaten direkt durch das EPA.",
      "Das europäische Patent verleiht in jedem Vertragsstaat, für den es erteilt wurde, dieselben Rechte wie ein in diesem Staat erteiltes nationales Patent.",
      "Verletzungsklagen aus dem europäischen Patent werden ausschließlich vor dem EPA verhandelt.",
      "Das europäische Patent schützt nur Erzeugnisse, nicht Verfahren.",
    ],
    answers: [1],
    explanation:
      "Art. 64 Abs. 1 EPÜ: Das europäische Patent verleiht seinem Inhaber in jedem Vertragsstaat, für den es erteilt wurde, vorbehaltlich des Art. 64 Abs. 3 EPÜ dieselben Rechte, die ihm ein in diesem Staat erteiltes nationales Patent verleihen würde. Das EPÜ schafft kein einheitliches supranationales Recht; Verletzungsklagen werden vor nationalen Gerichten geführt. Art. 64 Abs. 2 EPÜ stellt klar, dass auch Verfahrenspatente Erzeugnisschutz für direkt hergestellte Erzeugnisse gewähren.",
    sourceIds: ["art64epc"],
    sources: [
      { id: "art64epc", label: "Art. 64 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a64.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["eqe", "schutzbereich", "wirkung", "art64"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-015",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Schutzbereich — Art. 67 Vorläufiger Schutz",
    title: "Vorläufiger Schutz nach Veröffentlichung (Art. 67 EPÜ)",
    prompt:
      "Ab wann und unter welcher Voraussetzung entfaltet eine europäische Patentanmeldung nach Art. 67 EPÜ vorläufigen Schutz?",
    options: [
      "Ab Einreichung der Anmeldung, ohne weitere Voraussetzungen.",
      "Ab Erteilung des Patents, rückwirkend auf den Anmeldetag.",
      "Ab Veröffentlichung der Anmeldung; die Schutzwirkung im jeweiligen Vertragsstaat kann davon abhängen, dass eine Übersetzung in die dortige Amtssprache vorliegt.",
      "Ab Einreichung des Prüfungsantrags gemäß Art. 94 EPÜ.",
    ],
    answers: [2],
    explanation:
      "Art. 67 Abs. 1 EPÜ: Die europäische Patentanmeldung gewährt ab dem Zeitpunkt ihrer Veröffentlichung in jedem benannten Vertragsstaat vorläufigen Schutz. Art. 67 Abs. 3 EPÜ erlaubt den Vertragsstaaten zu verlangen, dass eine Übersetzung der Ansprüche in die Landessprache der Öffentlichkeit zugänglich gemacht wird — andernfalls greift der vorläufige Schutz dort nicht. Der vorläufige Schutz endet mit Erteilung oder Zurückweisung der Anmeldung.",
    sourceIds: ["art67epc"],
    sources: [
      { id: "art67epc", label: "Art. 67 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a67.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["eqe", "schutzbereich", "wirkung", "art67"],
    reviewStatus: "beta-ready",
  },

  // ─── MC — Art. 69 + Auslegungsprotokoll ─────────────────────────────────

  {
    id: "eqe-frist-016",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Schutzbereich — Art. 69 + Protokoll",
    title: "Schutzbereich: Grundregel Art. 69 EPÜ",
    prompt:
      "Was bestimmt Art. 69 EPÜ über den Schutzbereich eines europäischen Patents?",
    options: [
      "Der Schutzbereich wird durch den Wortlaut der Patentschrift bestimmt, wobei Beschreibung und Zeichnungen nur zur Interpretation von Mehrdeutigkeiten herangezogen werden dürfen.",
      "Der Schutzbereich wird durch den Inhalt der Patentansprüche bestimmt; Beschreibung und Zeichnungen dienen zur Auslegung der Ansprüche.",
      "Der Schutzbereich entspricht stets der erfinderischen Idee, wie sie der Erfinder in der Beschreibung definiert hat.",
      "Der Schutzbereich wird allein durch das Auslegungsprotokoll festgelegt, unabhängig vom Anspruchswortlaut.",
    ],
    answers: [1],
    explanation:
      "Art. 69 Abs. 1 S. 1 EPÜ: Der Schutzbereich des europäischen Patents und der europäischen Patentanmeldung wird durch den Inhalt der Patentansprüche bestimmt. Art. 69 Abs. 1 S. 2 EPÜ: Beschreibung und Zeichnungen sind jedoch zur Auslegung der Patentansprüche heranzuziehen. Das Auslegungsprotokoll (Art. 1) präzisiert, dass weder eine streng wortwörtliche noch eine rein auf die erfinderische Idee abstellende Auslegung gilt, sondern eine Auslegung, die beiden Extremen gerecht wird.",
    sourceIds: ["art69epc"],
    sources: [
      { id: "art69epc", label: "Art. 69 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a69.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "schutzbereich", "art69", "auslegungsprotokoll"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-017",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Schutzbereich — Art. 69 + Protokoll",
    title: "Auslegungsprotokoll Art. 1: Weder eng noch rein richtlinienbasiert",
    prompt:
      "Das Auslegungsprotokoll zu Art. 69 EPÜ, Art. 1, beschreibt zwei verworfene Extrempositionen. Welche sind das?",
    options: [
      "Die Auslegung nach dem nationalen Patentrecht des jeweiligen Vertragsstaats und die Auslegung nach dem PCT.",
      "Die streng wortlautgebundene Auslegung (nur genaue Worte schützen) und die Richtlinien-Auslegung (Ansprüche als grobe Richtlinie, Schutz am Gesamtkonzept orientiert).",
      "Die Auslegung nach dem Einreichungstag und die Auslegung nach dem Prioritätstag.",
      "Die literale Auslegung und die teleologische Auslegung.",
    ],
    answers: [1],
    explanation:
      "Protokoll über die Auslegung des Art. 69 EPÜ, Art. 1: Art. 69 EPÜ ist nicht in der Weise auszulegen, dass unter dem Schutzbereich des europäischen Patents der Schutz zu verstehen ist, der sich aus dem genauen Wortlaut der Patentansprüche ergibt (strenge Wortlautauslegung, 'fence post view'), und dass die Beschreibung und die Zeichnungen nur zur Behebung von Unklarheiten in den Patentansprüchen dienen. Ebenso wenig soll die Auslegung ergeben, dass die Patentansprüche nur als Richtlinie dienen und der Schutz sich auch auf das erstreckt, was der Fachmann nach Prüfung der Beschreibung und Zeichnungen als vom Patentinhaber gewünscht ansieht ('signpost view'). Vielmehr soll eine faire Auslegung zwischen diesen Extremen gelten.",
    sourceIds: ["art69epc"],
    sources: [
      { id: "art69epc", label: "Art. 69 EPÜ + Protokoll", url: "https://www.epo.org/en/legal/epc/2020/a69.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["eqe", "schutzbereich", "art69", "auslegungsprotokoll"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-018",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Schutzbereich — Art. 69 + Protokoll Art. 2 Äquivalente",
    title: "Äquivalente nach Auslegungsprotokoll Art. 2",
    prompt:
      "Welche Rolle spielen Äquivalente bei der Bestimmung des Schutzbereichs nach dem Auslegungsprotokoll Art. 2?",
    options: [
      "Äquivalente werden im EPÜ nicht berücksichtigt; die Doktrin gilt nur im nationalen Recht.",
      "Äquivalente sind bei der Bestimmung des Schutzbereichs angemessen zu berücksichtigen, d.h. der Patentinhaber soll nicht auf den buchstäblichen Wortlaut beschränkt werden.",
      "Äquivalente erweitern den Schutzbereich automatisch auf alle denkbaren technischen Varianten.",
      "Äquivalente können nur bei Verfahrensansprüchen herangezogen werden.",
    ],
    answers: [1],
    explanation:
      "Protokoll über die Auslegung des Art. 69 EPÜ, Art. 2 (Äquivalente): Bei der Bestimmung des Schutzbereichs des europäischen Patents sind Elemente, die äquivalent zu den in den Ansprüchen definierten Elementen sind, angemessen zu berücksichtigen. Dies bedeutet: Der Schutz endet nicht strikt am Wortlaut; Elemente, die dieselbe Funktion auf im Wesentlichen gleiche Weise erzielen und das gleiche Ergebnis liefern, können ebenfalls unter den Schutzbereich fallen. Der genaue Umfang der Äquivalenzlehre liegt bei nationalen Gerichten.",
    sourceIds: ["art69epc"],
    sources: [
      { id: "art69epc", label: "Art. 69 EPÜ + Protokoll Art. 2", url: "https://www.epo.org/en/legal/epc/2020/a69.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["eqe", "schutzbereich", "art69", "aequivalente", "auslegungsprotokoll"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-019",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Schutzbereich — Art. 69 und Art. 123(3) EPÜ",
    title: "Art. 69 und Art. 123(3) EPÜ im Zusammenspiel (Fallkonstellation)",
    prompt:
      "Patentinhaber P möchte nach Erteilung einen Anspruch ändern, um einen klareren Wortlaut zu erhalten. Der neue Wortlaut würde nach Art. 69 EPÜ einen breiteren Schutzbereich ergeben als der ursprüngliche erteilte Anspruch. Ist diese Änderung zulässig?",
    options: [
      "Ja, weil Art. 69 EPÜ immer den weitestmöglichen Schutz gebietet.",
      "Nein — Art. 123 Abs. 3 EPÜ untersagt jede Änderung, die den Schutzbereich nach Art. 69 EPÜ erweitert.",
      "Ja, solange die Änderung durch die ursprüngliche Offenbarung gedeckt ist.",
      "Die Zulässigkeit richtet sich allein nach Art. 84 EPÜ.",
    ],
    answers: [1],
    explanation:
      "Art. 123 Abs. 3 EPÜ: Das europäische Patent darf nicht in der Weise geändert werden, dass sein Schutzbereich erweitert wird. Der Schutzbereich wird nach Art. 69 EPÜ bestimmt. Eine Änderung nach Erteilung, die zu einem breiteren Schutzbereich führt, ist daher absolut unzulässig — unabhängig davon, ob die Änderung durch die Offenbarung gedeckt ist (Art. 123 Abs. 2 EPÜ) oder Klarheit verbessert (Art. 84 EPÜ). Diese Schranke schützt Dritte, die auf den erteilten Schutzbereich vertrauen.",
    sourceIds: ["art69epc", "art123epc"],
    sources: [
      { id: "art69epc", label: "Art. 69 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a69.html" },
      { id: "art123epc", label: "Art. 123 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a123.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["eqe", "schutzbereich", "art69", "art123", "aenderungen"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-020",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "multi",
    topic: "EPÜ Schutzbereich — Art. 69 + Art. 84 EPÜ",
    title: "Art. 84 und Art. 69 EPÜ: Unterschied (Mehrfachauswahl)",
    prompt:
      "Welche der folgenden Aussagen zum Verhältnis von Art. 84 und Art. 69 EPÜ sind zutreffend? (Mehrere Antworten möglich.)",
    options: [
      "Art. 84 EPÜ verlangt, dass Ansprüche klar und knapp abgefasst sind und durch die Beschreibung gestützt werden — er gilt im Erteilungsverfahren.",
      "Art. 69 EPÜ definiert den Schutzbereich des erteilten Patents und ist primär für die Verletzungsprüfung relevant.",
      "Art. 84 EPÜ ist ein selbstständiger Einspruchsgrund nach Art. 100 EPÜ.",
      "Klarheitsmängel nach Art. 84 EPÜ können im Einspruchsverfahren nur insoweit angegriffen werden, als sie durch eine Änderung eingeführt wurden.",
    ],
    answers: [0, 1, 3],
    explanation:
      "Art. 84 EPÜ (Ansprüche müssen klar, knapp und durch die Beschreibung gestützt sein) gilt im Erteilungsverfahren und ist Prüfungsmaßstab des EPA. Art. 69 EPÜ bestimmt den Schutzbereich des erteilten Patents; er ist der Maßstab für die Verletzungsprüfung vor nationalen Gerichten und für Art. 123 Abs. 3 EPÜ. Wichtig: Art. 84 EPÜ ist kein eigenständiger Einspruchsgrund (Art. 100 EPÜ nennt ihn nicht). Im Einspruchsverfahren können Klarheitsmängel nur angegriffen werden, soweit sie durch eine nach der Erteilung vorgenommene Änderung neu entstanden sind (G 3/14).",
    sourceIds: ["art84epc", "art69epc"],
    sources: [
      { id: "art84epc", label: "Art. 84 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a84.html" },
      { id: "art69epc", label: "Art. 69 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a69.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["eqe", "schutzbereich", "art69", "art84"],
    reviewStatus: "beta-ready",
  },

  // ─── FLASHCARDS ───────────────────────────────────────────────────────────

  {
    id: "eqe-frist-021",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ Fristen — Art. 121 / Art. 122 EPÜ",
    title: "Weiterbehandlung vs. Wiedereinsetzung: Kernunterschiede",
    prompt:
      "Nennen Sie die drei wesentlichen Unterschiede zwischen Weiterbehandlung (Art. 121 EPÜ) und Wiedereinsetzung (Art. 122 EPÜ).",
    options: [],
    answers: [],
    explanation:
      "1. Sorgfaltsnachweis: Weiterbehandlung (Art. 121 EPÜ) erfordert keinen Sorgfaltsnachweis; Wiedereinsetzung (Art. 122 EPÜ) setzt voraus, dass der Antragsteller trotz aller gebotenen Sorgfalt ('all due care') gehindert war. 2. Antragsfrist: Weiterbehandlung: 2 Monate ab Hinweis auf Rechtsverlust (R. 135 Abs. 1 EPÜ). Wiedereinsetzung: 2 Monate ab Wegfall des Hindernisses, maximal 12 Monate ab Fristablauf (R. 136 Abs. 1 EPÜ). 3. Anwendungsbereich: Weiterbehandlung ist auf Anmeldungsfristen beschränkt (nicht nach Erteilung); Wiedereinsetzung gilt auch für Patentinhaber (z.B. Beschwerdebegründungsfrist) — jeweils vorbehaltlich der Ausschlusslisten.",
    sourceIds: ["art121epc", "art122epc"],
    sources: [
      { id: "art121epc", label: "Art. 121 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a121.html" },
      { id: "art122epc", label: "Art. 122 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a122.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["eqe", "fristen", "weiterbehandlung", "wiedereinsetzung", "art121", "art122"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-022",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ Fristen — R. 131 / R. 134 Fristberechnung",
    title: "Fristberechnung: Grundregeln R. 131 EPÜ",
    prompt:
      "Wie werden nach R. 131 EPÜ Monatsfristen berechnet, und was gilt wenn der letzte Tag auf einen Feiertag/Ausfalltag fällt?",
    options: [],
    answers: [],
    explanation:
      "R. 131 Abs. 2 EPÜ: Der Lauf einer Frist beginnt am Tag nach dem Ereignis (z.B. Zustellung einer Mitteilung) — der Ereignistag selbst zählt nicht mit ('dies a quo non computatur'). R. 131 Abs. 4 EPÜ: Monatliche/jährliche Fristen enden am entsprechend benannten Tag des letzten Monats; gibt es diesen Tag nicht, am letzten Tag des Monats. R. 134 Abs. 1 EPÜ: Fällt das Fristende auf einen Tag, an dem das EPA nicht für die Entgegennahme von Schriftstücken geöffnet ist (offizielle Ausfalltage, Samstag, Sonntag), verlängert sich die Frist bis zum nächsten Öffnungstag.",
    sourceIds: ["r131epc", "r134epc"],
    sources: [
      { id: "r131epc", label: "R. 131 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r131.html" },
      { id: "r134epc", label: "R. 134 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r134.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "fristen", "fristberechnung", "r131", "r134"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-023",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ Schutzbereich — Art. 69 EPÜ",
    title: "Art. 69 EPÜ: Schutzbereich und Auslegungsprotokoll",
    prompt:
      "Beschreiben Sie den Inhalt von Art. 69 EPÜ und die Kernaussage des Auslegungsprotokolls (Art. 1 und Art. 2).",
    options: [],
    answers: [],
    explanation:
      "Art. 69 Abs. 1 EPÜ: Der Schutzbereich des europäischen Patents wird durch den Inhalt der Patentansprüche bestimmt; Beschreibung und Zeichnungen dienen zur Auslegung der Ansprüche. Protokoll Art. 1: Es gilt weder die strenge Wortlautauslegung (nur exakte Worte schützen) noch die reine Richtlinien-Auslegung (Ansprüche als bloße Orientierung); vielmehr ist eine faire Auslegung anzustreben, die dem Patentinhaber angemessenen Schutz gibt und Dritten hinreichende Rechtssicherheit bietet. Protokoll Art. 2: Elemente, die zu den in den Ansprüchen definierten Elementen äquivalent sind, sind bei der Bestimmung des Schutzbereichs angemessen zu berücksichtigen.",
    sourceIds: ["art69epc"],
    sources: [
      { id: "art69epc", label: "Art. 69 EPÜ + Protokoll", url: "https://www.epo.org/en/legal/epc/2020/a69.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "schutzbereich", "art69", "auslegungsprotokoll"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-024",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ Fristen — Art. 86 / R. 51 Jahresgebühren",
    title: "Jahresgebühren: Fälligkeitsschema und Nachfrist",
    prompt:
      "Wann werden Jahresgebühren nach R. 51 EPÜ fällig, und welche Nachfristmöglichkeit besteht?",
    options: [],
    answers: [],
    explanation:
      "R. 51 Abs. 1 EPÜ: Jahresgebühren für europäische Patentanmeldungen werden am letzten Tag des Monats fällig, der dem Monat entspricht, in dem der Anmeldetag liegt (Jahrestag des Anmeldetags, bezogen auf den jeweiligen Jahrestag). Für das dritte Schutzjahr ist erstmals eine Jahresgebühr zu zahlen. R. 51 Abs. 2 EPÜ: Jahresgebühren können noch innerhalb von sechs Monaten nach ihrer Fälligkeit mit dem vorgeschriebenen Verspätungszuschlag nachentrichtet werden. Art. 86 Abs. 3 EPÜ: Wird auch die Nachfrist nicht eingehalten, gilt die Anmeldung als zurückgenommen.",
    sourceIds: ["art86epc", "r51epc"],
    sources: [
      { id: "art86epc", label: "Art. 86 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a86.html" },
      { id: "r51epc", label: "R. 51 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r51.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "fristen", "jahresgebuehren", "art86"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-025",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ Schutzbereich — Art. 63 / Art. 64 EPÜ",
    title: "Laufzeit und Wirkung: Art. 63 und Art. 64 EPÜ",
    prompt:
      "Fassen Sie die Kernaussagen von Art. 63 (Laufzeit) und Art. 64 (Wirkung) EPÜ zusammen.",
    options: [],
    answers: [],
    explanation:
      "Art. 63 Abs. 1 EPÜ: Die Laufzeit des europäischen Patents beträgt 20 Jahre, gerechnet ab dem Anmeldetag der europäischen Patentanmeldung. Art. 64 Abs. 1 EPÜ: Das europäische Patent verleiht seinem Inhaber ab dem Tag der Bekanntmachung der Erteilung in jedem Vertragsstaat, für den es erteilt wurde, dieselben Rechte, die ihm ein nationales Patent dieses Staates verleihen würde. Art. 64 Abs. 2 EPÜ: Erstreckt sich das Patent auf ein Verfahren, so umfasst der Schutz auch die unmittelbar durch dieses Verfahren hergestellten Erzeugnisse. Art. 64 Abs. 3 EPÜ: Verletzungsklagen unterliegen dem nationalen Recht.",
    sourceIds: ["art63epc", "art64epc"],
    sources: [
      { id: "art63epc", label: "Art. 63 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a63.html" },
      { id: "art64epc", label: "Art. 64 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a64.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "schutzbereich", "laufzeit", "wirkung", "art63", "art64"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-026",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ Fristen — All due care (Art. 122 EPÜ)",
    title: "All due care: Was wird verlangt?",
    prompt:
      "Was versteht die Beschwerdekammer des EPA unter dem Maßstab 'alle gebotene Sorgfalt' (all due care) bei Art. 122 EPÜ?",
    options: [],
    answers: [],
    explanation:
      "Art. 122 Abs. 1 EPÜ: Die Wiedereinsetzung setzt voraus, dass der Anmelder 'trotz Beachtung aller gebotenen Sorgfalt' verhindert war, die Frist einzuhalten. In der EPA-Rechtsprechung bedeutet dies: (1) Der Anmelder und seine Vertreter müssen ein zuverlässiges Fristüberwachungssystem eingerichtet haben. (2) Dieses System muss im Einzelfall auch korrekt funktioniert haben (kein menschliches Versagen ist per se entschuldigt — maßgeblich ist, ob das Versagen trotz ordnungsgemäßem System unvorhersehbar war). (3) Bei Bürofehlern: Es muss ein üblicherweise zuverlässiges System vorgelegen haben, und der Fehler muss eine seltene Ausnahme darstellen.",
    sourceIds: ["art122epc"],
    sources: [
      { id: "art122epc", label: "Art. 122 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a122.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["eqe", "fristen", "wiedereinsetzung", "art122", "all-due-care"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-027",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ Schutzbereich — Art. 67 EPÜ Vorläufiger Schutz",
    title: "Vorläufiger Schutz: Inhalt und Grenzen",
    prompt:
      "Was ist der genaue Inhalt des vorläufigen Schutzes nach Art. 67 EPÜ und wo liegen seine Grenzen?",
    options: [],
    answers: [],
    explanation:
      "Art. 67 Abs. 1 EPÜ: Ab dem Zeitpunkt der Veröffentlichung der europäischen Patentanmeldung (i.d.R. 18 Monate nach dem frühesten Prioritätsdatum, Art. 93 Abs. 1 EPÜ) genießt die Anmeldung in jedem benannten Vertragsstaat vorläufigen Schutz. Art. 67 Abs. 3 EPÜ: Jeder Vertragsstaat kann verlangen, dass der Schutz erst nach Zugang einer Übersetzung der Ansprüche in einer Amtssprache dieses Staates wirkt. Art. 67 Abs. 4 EPÜ: Die Anmeldung entfaltet nach Ablehnung oder Zurücknahme keine vorläufige Schutzwirkung mehr. Grenze: Der vorläufige Schutz ist schwächer als das erteilte Patent — er kann keine vollstreckbaren Ansprüche begründen, solange das Patent noch nicht erteilt ist.",
    sourceIds: ["art67epc"],
    sources: [
      { id: "art67epc", label: "Art. 67 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a67.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["eqe", "schutzbereich", "wirkung", "art67"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-028",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "EPÜ Fristen — Prüfungsschema Rechtsverlust",
    title: "Prüfungsschema: Fristversäumnis und Rechtsbehelfe",
    prompt:
      "In welcher Reihenfolge ist bei einem Fristversäumnis gegenüber dem EPA vorzugehen (Prüfungsschema)?",
    options: [],
    answers: [],
    explanation:
      "Prüfungsschema bei Fristversäumnis: 1. Ist die Frist überhaupt versäumt (oder wurde sie durch R. 134 EPÜ verlängert)? 2. Ist Weiterbehandlung nach Art. 121 EPÜ möglich? — Prüfe: Ist die Frist nicht nach R. 135 Abs. 2 EPÜ ausgeschlossen? Wenn ja: Antrag + Nachholung + Gebühr innerhalb von 2 Monaten nach Rechtsverlusthinweis (R. 135 Abs. 1 EPÜ). 3. Wenn Weiterbehandlung ausgeschlossen oder deren Frist verstrichen: Ist Wiedereinsetzung nach Art. 122 EPÜ möglich? — Prüfe: all due care + Antrag innerhalb von 2 Monaten ab Wegfall des Hindernisses + max. 12 Monate ab Fristablauf (R. 136 Abs. 1 EPÜ) + Frist nicht nach R. 136 Abs. 3 EPÜ ausgeschlossen. 4. Wenn beides scheitert: Rechtsverlust endgültig.",
    sourceIds: ["art121epc", "art122epc"],
    sources: [
      { id: "art121epc", label: "Art. 121 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a121.html" },
      { id: "art122epc", label: "Art. 122 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a122.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Prüfungsschema",
    skillTags: ["eqe", "fristen", "pruefungsschema", "weiterbehandlung", "wiedereinsetzung"],
    reviewStatus: "beta-ready",
  },

  // ─── ORDERINGS ────────────────────────────────────────────────────────────

  {
    id: "eqe-frist-029",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "ordering",
    topic: "EPÜ Fristen — Prüfungsschema Fristversäumnis",
    title: "Rechtsbehelf bei Fristversäumnis: richtige Reihenfolge",
    prompt:
      "Bringen Sie die folgenden Schritte bei einem Fristversäumnis gegenüber dem EPA in die richtige Reihenfolge.",
    options: [
      "Prüfen, ob Weiterbehandlung nach Art. 121 EPÜ möglich ist (Frist nicht nach R. 135(2) ausgeschlossen)",
      "Feststellen, dass eine Frist gegenüber dem EPA versäumt wurde",
      "Subsidiär prüfen, ob Wiedereinsetzung nach Art. 122 EPÜ möglich ist (all due care + Fristen R. 136(1))",
      "Weiterbehandlung: Antrag + Nachholung + Gebühr innerhalb 2 Monate nach Rechtsverlusthinweis",
      "Wenn beides nicht greift: Rechtsverlust endgültig akzeptieren",
    ],
    answers: [1, 0, 3, 2, 4],
    orderItems: [
      "Feststellen, dass eine Frist gegenüber dem EPA versäumt wurde",
      "Prüfen, ob Weiterbehandlung nach Art. 121 EPÜ möglich ist (Frist nicht nach R. 135(2) ausgeschlossen)",
      "Weiterbehandlung: Antrag + Nachholung + Gebühr innerhalb 2 Monate nach Rechtsverlusthinweis",
      "Subsidiär prüfen, ob Wiedereinsetzung nach Art. 122 EPÜ möglich ist (all due care + Fristen R. 136(1))",
      "Wenn beides nicht greift: Rechtsverlust endgültig akzeptieren",
    ],
    explanation:
      "Das Prüfungsschema folgt der Subsidiaritätslogik: Erst wird festgestellt, dass überhaupt ein Rechtsverlust eingetreten ist. Dann wird Weiterbehandlung (Art. 121 EPÜ) als einfacherer, sorgfaltsfreier Rechtsbehelf geprüft. Ist dieser zugänglich, ist der Antrag innerhalb von 2 Monaten nach dem Hinweis auf den Rechtsverlust zu stellen (R. 135 Abs. 1 EPÜ). Erst wenn Weiterbehandlung scheitert, kommt die anspruchsvollere Wiedereinsetzung (Art. 122 EPÜ) in Betracht. Ist auch diese ausgeschlossen, ist der Rechtsverlust endgültig.",
    sourceIds: ["art121epc", "art122epc", "r135epc", "r136epc"],
    sources: [
      { id: "art121epc", label: "Art. 121 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a121.html" },
      { id: "art122epc", label: "Art. 122 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a122.html" },
      { id: "r135epc", label: "R. 135 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r135.html" },
      { id: "r136epc", label: "R. 136 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r136.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Prüfungsschema",
    skillTags: ["eqe", "fristen", "pruefungsschema", "weiterbehandlung", "wiedereinsetzung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-030",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "ordering",
    topic: "EPÜ Fristen — Wiedereinsetzungsprüfung Art. 122 EPÜ",
    title: "Prüfung der Wiedereinsetzung: chronologische Reihenfolge",
    prompt:
      "Bringen Sie die Prüfungsschritte einer Wiedereinsetzung nach Art. 122 EPÜ in die richtige Reihenfolge.",
    options: [
      "Prüfen, ob die 12-Monats-Höchstfrist ab Fristablauf eingehalten ist (R. 136(1) S. 2 EPÜ)",
      "Prüfen, ob die versäumte Frist nicht nach R. 136(3) EPÜ von der Wiedereinsetzung ausgeschlossen ist",
      "Prüfen, ob der Antragsteller trotz aller gebotenen Sorgfalt verhindert war (all due care, Art. 122(1) EPÜ)",
      "Prüfen, ob der Antrag innerhalb von 2 Monaten nach Wegfall des Hindernisses gestellt wurde (R. 136(1) S. 1 EPÜ)",
      "Versäumte Handlung nachholen und Wiedereinsetzungsgebühr zahlen",
    ],
    answers: [1, 0, 3, 2, 4],
    orderItems: [
      "Prüfen, ob die versäumte Frist nicht nach R. 136(3) EPÜ von der Wiedereinsetzung ausgeschlossen ist",
      "Prüfen, ob der Antragsteller trotz aller gebotenen Sorgfalt verhindert war (all due care, Art. 122(1) EPÜ)",
      "Prüfen, ob der Antrag innerhalb von 2 Monaten nach Wegfall des Hindernisses gestellt wurde (R. 136(1) S. 1 EPÜ)",
      "Prüfen, ob die 12-Monats-Höchstfrist ab Fristablauf eingehalten ist (R. 136(1) S. 2 EPÜ)",
      "Versäumte Handlung nachholen und Wiedereinsetzungsgebühr zahlen",
    ],
    explanation:
      "Die Wiedereinsetzungsprüfung beginnt mit dem Ausschlusskatalog (R. 136 Abs. 3 EPÜ) — ist die Frist ausgeschlossen, erübrigen sich alle weiteren Schritte. Dann folgt die materielle Prüfung des all-due-care-Maßstabs (Art. 122 Abs. 1 EPÜ). Anschließend werden die Antragsfristen geprüft: 2 Monate ab Wegfall des Hindernisses (R. 136 Abs. 1 S. 1 EPÜ) und 12 Monate ab Fristablauf als Höchstgrenze (R. 136 Abs. 1 S. 2 EPÜ). Abschließend muss die versäumte Handlung nachgeholt und die Gebühr gezahlt werden.",
    sourceIds: ["art122epc", "r136epc"],
    sources: [
      { id: "art122epc", label: "Art. 122 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a122.html" },
      { id: "r136epc", label: "R. 136 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r136.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Prüfungsschema",
    skillTags: ["eqe", "fristen", "wiedereinsetzung", "pruefungsschema", "art122"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-031",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "ordering",
    topic: "EPÜ Schutzbereich — Art. 69 EPÜ Auslegungsschritte",
    title: "Schutzbereichsbestimmung nach Art. 69 EPÜ: Auslegungsschritte",
    prompt:
      "Bringen Sie die Schritte zur Bestimmung des Schutzbereichs nach Art. 69 EPÜ in die richtige methodische Reihenfolge.",
    options: [
      "Beschreibung und Zeichnungen zur Auslegung von Bedeutung und Umfang der Anspruchsmerkmale heranziehen",
      "Wortlaut der Patentansprüche als Ausgangspunkt bestimmen",
      "Prüfen, ob das angegriffene Element unter den so bestimmten Schutzbereich fällt (literal oder äquivalent, Protokoll Art. 2)",
      "Faire Auslegung nach Protokoll Art. 1 vornehmen: weder strenge Wortlautauslegung noch bloße Richtlinie",
    ],
    answers: [1, 0, 3, 2],
    orderItems: [
      "Wortlaut der Patentansprüche als Ausgangspunkt bestimmen",
      "Beschreibung und Zeichnungen zur Auslegung von Bedeutung und Umfang der Anspruchsmerkmale heranziehen",
      "Faire Auslegung nach Protokoll Art. 1 vornehmen: weder strenge Wortlautauslegung noch bloße Richtlinie",
      "Prüfen, ob das angegriffene Element unter den so bestimmten Schutzbereich fällt (literal oder äquivalent, Protokoll Art. 2)",
    ],
    explanation:
      "Methodischer Ausgangspunkt nach Art. 69 Abs. 1 S. 1 EPÜ ist der Wortlaut der Ansprüche. Beschreibung und Zeichnungen werden dann zur Auslegung (Bedeutungsermittlung der Merkmale) herangezogen (Art. 69 Abs. 1 S. 2 EPÜ). Die Auslegung selbst orientiert sich am Protokoll Art. 1 (faire Mitte). Erst danach wird geprüft, ob das angegriffene Erzeugnis/Verfahren den so bestimmten Schutzbereich verletzt — zunächst literal, dann ggf. unter Einbeziehung von Äquivalenten (Protokoll Art. 2).",
    sourceIds: ["art69epc"],
    sources: [
      { id: "art69epc", label: "Art. 69 EPÜ + Protokoll", url: "https://www.epo.org/en/legal/epc/2020/a69.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Prüfungsschema",
    skillTags: ["eqe", "schutzbereich", "art69", "auslegungsprotokoll", "pruefungsschema"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-032",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "ordering",
    topic: "EPÜ Fristen — Jahresgebühren Fristablauf",
    title: "Jahresgebühren: Schritte bei drohender Fristversäumnis",
    prompt:
      "Bringen Sie die Handlungsschritte bei einem drohenden Rechtsverlust wegen nicht bezahlter Jahresgebühr in die richtige Reihenfolge.",
    options: [
      "Prüfen, ob Wiedereinsetzung nach Art. 122 EPÜ möglich ist (all due care)",
      "Jahresgebühr + Verspätungszuschlag innerhalb der 6-Monats-Nachfrist zahlen (R. 51(2) EPÜ)",
      "Feststellen, dass die Jahresgebühr am Fälligkeitstag nicht gezahlt wurde",
      "Wenn Nachfrist abgelaufen: Anmeldung/Patent gilt als zurückgenommen / erloschen (Art. 86(3) EPÜ)",
    ],
    answers: [2, 1, 3, 0],
    orderItems: [
      "Feststellen, dass die Jahresgebühr am Fälligkeitstag nicht gezahlt wurde",
      "Jahresgebühr + Verspätungszuschlag innerhalb der 6-Monats-Nachfrist zahlen (R. 51(2) EPÜ)",
      "Wenn Nachfrist abgelaufen: Anmeldung/Patent gilt als zurückgenommen / erloschen (Art. 86(3) EPÜ)",
      "Prüfen, ob Wiedereinsetzung nach Art. 122 EPÜ möglich ist (all due care)",
    ],
    explanation:
      "Erste Feststellung: Jahresgebühr ist nicht fristgerecht gezahlt worden. Zweiter Schritt: Innerhalb der 6-Monats-Nachfrist (R. 51 Abs. 2 EPÜ) Gebühr + Verspätungszuschlag zahlen — das ist die einfachste Lösung. Wenn die Nachfrist ebenfalls abläuft: Die Anmeldung gilt als zurückgenommen (Art. 86 Abs. 3 EPÜ). Danach kann noch Wiedereinsetzung nach Art. 122 EPÜ geprüft werden, wenn die Versäumnis trotz aller gebotenen Sorgfalt eingetreten ist.",
    sourceIds: ["art86epc", "r51epc", "art122epc"],
    sources: [
      { id: "art86epc", label: "Art. 86 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a86.html" },
      { id: "r51epc", label: "R. 51 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r51.html" },
      { id: "art122epc", label: "Art. 122 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a122.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Prüfungsschema",
    skillTags: ["eqe", "fristen", "jahresgebuehren", "pruefungsschema", "art86"],
    reviewStatus: "beta-ready",
  },

  // ─── CLOZE ────────────────────────────────────────────────────────────────

  {
    id: "eqe-frist-033",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "cloze",
    topic: "EPÜ Fristen — Art. 122 EPÜ Wiedereinsetzung",
    title: "Cloze: 2-Monats-Frist Wiedereinsetzung",
    prompt:
      "Der Antrag auf Wiedereinsetzung ist nach R. 136 Abs. 1 S. 1 EPÜ innerhalb von ___ nach Wegfall des Hindernisses zu stellen.",
    options: [
      "zwei Wochen",
      "einem Monat",
      "zwei Monaten",
      "sechs Monaten",
    ],
    answers: [2],
    explanation:
      "R. 136 Abs. 1 S. 1 EPÜ: Der Antrag auf Wiedereinsetzung ist innerhalb von zwei Monaten nach Wegfall des Hindernisses einzureichen. Gleichzeitig ist die versäumte Handlung nachzuholen. Zwei Wochen und ein Monat sind zu kurz; sechs Monate gilt für die Jahresgebühren-Nachfrist (R. 51 Abs. 2 EPÜ), nicht für die Wiedereinsetzung.",
    sourceIds: ["r136epc"],
    sources: [
      { id: "r136epc", label: "R. 136 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r136.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Fristwert",
    skillTags: ["eqe", "fristen", "wiedereinsetzung", "art122", "fristwert"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-034",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "cloze",
    topic: "EPÜ Fristen — Art. 122 EPÜ Höchstfrist",
    title: "Cloze: 12-Monats-Höchstfrist Wiedereinsetzung",
    prompt:
      "Neben der 2-Monats-Frist darf der Wiedereinsetzungsantrag nach R. 136 Abs. 1 S. 2 EPÜ höchstens ___ nach Ablauf der versäumten Frist eingereicht werden.",
    options: [
      "6 Monaten",
      "12 Monaten",
      "18 Monaten",
      "24 Monaten",
    ],
    answers: [1],
    explanation:
      "R. 136 Abs. 1 S. 2 EPÜ: Unabhängig von der 2-Monats-Frist ab Wegfall des Hindernisses gilt eine absolute 12-Monats-Höchstfrist ab dem Tag, an dem die ursprüngliche Frist abgelaufen ist. 6 Monate ist die Nachfrist für Jahresgebühren (R. 51 Abs. 2 EPÜ). 18 und 24 Monate existieren in diesem Kontext nicht.",
    sourceIds: ["r136epc"],
    sources: [
      { id: "r136epc", label: "R. 136 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r136.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Fristwert",
    skillTags: ["eqe", "fristen", "wiedereinsetzung", "art122", "fristwert"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-035",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "cloze",
    topic: "EPÜ Fristen — R. 51 Jahresgebühren Nachfrist",
    title: "Cloze: 6-Monats-Nachfrist Jahresgebühr",
    prompt:
      "Jahresgebühren können nach R. 51 Abs. 2 EPÜ noch innerhalb von ___ nach ihrer Fälligkeit mit Verspätungszuschlag gezahlt werden.",
    options: [
      "2 Monaten",
      "4 Monaten",
      "6 Monaten",
      "12 Monaten",
    ],
    answers: [2],
    explanation:
      "R. 51 Abs. 2 EPÜ: Jahresgebühren können noch innerhalb von sechs Monaten nach ihrer Fälligkeit mit dem vorgeschriebenen Verspätungszuschlag nachentrichtet werden. 2 Monate ist die Frist für Weiterbehandlung (R. 135 Abs. 1 EPÜ) und für vom EPA gesetzte Mindestfristen (R. 132 Abs. 2 EPÜ). 4 Monate ist die Höchstdauer von EPA-gesetzten Fristen (R. 132 Abs. 2 EPÜ). 12 Monate ist die Höchstfrist für Wiedereinsetzung (R. 136 Abs. 1 S. 2 EPÜ).",
    sourceIds: ["r51epc"],
    sources: [
      { id: "r51epc", label: "R. 51 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r51.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Fristwert",
    skillTags: ["eqe", "fristen", "jahresgebuehren", "art86", "fristwert"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-036",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "cloze",
    topic: "EPÜ Schutzbereich — Art. 63 EPÜ Laufzeit",
    title: "Cloze: 20-jährige Laufzeit",
    prompt:
      "Das europäische Patent hat nach Art. 63 Abs. 1 EPÜ eine Laufzeit von ___ Jahren, gerechnet ab dem Anmeldetag.",
    options: [
      "10",
      "15",
      "20",
      "25",
    ],
    answers: [2],
    explanation:
      "Art. 63 Abs. 1 EPÜ: Die Laufzeit des europäischen Patents beträgt 20 Jahre, gerechnet ab dem Anmeldetag der europäischen Patentanmeldung. 10 und 15 Jahre entsprechen keiner EPÜ-Norm. 25 Jahre wären die theoretische Höchstdauer bei SPC-Verlängerung für bestimmte regulierte Produkte, sind aber kein Teil des EPÜ selbst.",
    sourceIds: ["art63epc"],
    sources: [
      { id: "art63epc", label: "Art. 63 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a63.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Fristwert",
    skillTags: ["eqe", "schutzbereich", "laufzeit", "art63", "fristwert"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-037",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "cloze",
    topic: "EPÜ Fristen — R. 132 Mindestfrist",
    title: "Cloze: Mindestfrist für EPA-gesetzte Fristen",
    prompt:
      "Fristen, die das EPA setzt, betragen nach R. 132 Abs. 2 EPÜ mindestens ___ Monate.",
    options: [
      "1",
      "2",
      "4",
      "6",
    ],
    answers: [1],
    explanation:
      "R. 132 Abs. 2 EPÜ: Vom EPA gesetzte Fristen betragen mindestens zwei Monate und höchstens vier Monate. Diese Mindestgarantie schützt den Anmelder vor zu kurzen Fristen. 1 Monat wäre zu kurz und unzulässig. 4 Monate ist die Höchstdauer. 6 Monate gilt für die Jahresgebühren-Nachfrist.",
    sourceIds: ["r132epc"],
    sources: [
      { id: "r132epc", label: "R. 132 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r132.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Fristwert",
    skillTags: ["eqe", "fristen", "fristberechnung", "r132", "fristwert"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-038",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "cloze",
    topic: "EPÜ Fristen — Art. 121 / R. 135 Weiterbehandlung",
    title: "Cloze: Frist für Weiterbehandlungsantrag",
    prompt:
      "Der Antrag auf Weiterbehandlung nach R. 135 Abs. 1 EPÜ muss innerhalb von ___ nach dem Hinweis auf den Rechtsverlust gestellt werden.",
    options: [
      "einem Monat",
      "zwei Monaten",
      "drei Monaten",
      "sechs Monaten",
    ],
    answers: [1],
    explanation:
      "R. 135 Abs. 1 EPÜ: Der Antrag auf Weiterbehandlung ist innerhalb von zwei Monaten nach dem Hinweis auf den Rechtsverlust einzureichen; gleichzeitig ist die versäumte Handlung nachzuholen und die Weiterbehandlungsgebühr zu zahlen. Ein Monat und drei Monate existieren nicht als gesetzliche Fristen in diesem Kontext. Sechs Monate gilt für die Jahresgebühren-Nachfrist (R. 51 Abs. 2 EPÜ).",
    sourceIds: ["r135epc"],
    sources: [
      { id: "r135epc", label: "R. 135 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r135.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Fristwert",
    skillTags: ["eqe", "fristen", "weiterbehandlung", "art121", "fristwert"],
    reviewStatus: "beta-ready",
  },

  // ─── Weitere MC — Fallkonstellationen und Prüfungsschema ─────────────────

  {
    id: "eqe-frist-039",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Schutzbereich — Art. 69 EPÜ Fallkonstellation",
    title: "Schutzbereich: Verletzung durch Äquivalent (Fallkonstellation)",
    prompt:
      "Patent P schützt eine Vorrichtung mit Merkmal X (Schraubenverbindung). Dritter D verwendet eine Klemmverbindung, die dieselbe Funktion auf im Wesentlichen gleiche Weise mit demselben Ergebnis erfüllt. Welche rechtliche Frage stellt sich nach Art. 69 EPÜ?",
    options: [
      "Ob D das Merkmal X wörtlich verwirklicht hat (literal infringement).",
      "Ob die Klemmverbindung ein zum Merkmal X äquivalentes Element ist, das nach Protokoll Art. 2 in den Schutzbereich fällt.",
      "Ob D eine Lizenz erworben hat.",
      "Ob das Patent noch in der Laufzeit liegt (Art. 63 EPÜ).",
    ],
    answers: [1],
    explanation:
      "Da D die Klemmverbindung statt der im Anspruch genannten Schraubenverbindung einsetzt, liegt keine wörtliche Verletzung vor. Entscheidend ist, ob die Klemmverbindung ein äquivalentes Element im Sinne von Protokoll Art. 2 zu Art. 69 EPÜ darstellt. Die drei klassischen Äquivalenzfragen lauten: (1) Löst das Element die gleiche Aufgabe? (2) Erzielt es das gleiche Ergebnis? (3) Würde ein Fachmann das Element als gleichwirkend erkennen? Lizenzen und Laufzeit sind von der Schutzbereichsfrage unabhängig.",
    sourceIds: ["art69epc"],
    sources: [
      { id: "art69epc", label: "Art. 69 EPÜ + Protokoll Art. 2", url: "https://www.epo.org/en/legal/epc/2020/a69.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["eqe", "schutzbereich", "art69", "aequivalente", "auslegungsprotokoll"],
    reviewStatus: "beta-ready",
  },

  {
    id: "eqe-frist-040",
    pathId: "epu-eqe",
    mode: "ausbildung",
    kind: "single",
    topic: "EPÜ Fristen — Prüfungsschema (Gesamtüberblick)",
    title: "Fristkaskade: Welcher Wert gilt für welche Norm? (Gesamtübersicht)",
    prompt:
      "Ordnen Sie zu: Welcher Fristwert ist korrekt der jeweiligen EPÜ-Norm zugeordnet?",
    options: [
      "R. 135(1) EPÜ: 6 Monate — R. 136(1) EPÜ: 2 Monate + 12 Monate — R. 51(2) EPÜ: 2 Monate — Art. 63(1) EPÜ: 20 Jahre",
      "R. 135(1) EPÜ: 2 Monate — R. 136(1) EPÜ: 2 Monate + 12 Monate — R. 51(2) EPÜ: 6 Monate — Art. 63(1) EPÜ: 20 Jahre",
      "R. 135(1) EPÜ: 2 Monate — R. 136(1) EPÜ: 6 Monate + 24 Monate — R. 51(2) EPÜ: 6 Monate — Art. 63(1) EPÜ: 25 Jahre",
      "R. 135(1) EPÜ: 2 Monate — R. 136(1) EPÜ: 2 Monate + 12 Monate — R. 51(2) EPÜ: 3 Monate — Art. 63(1) EPÜ: 20 Jahre",
    ],
    answers: [1],
    explanation:
      "Die korrekte Zuordnung: R. 135 Abs. 1 EPÜ (Weiterbehandlung): 2 Monate ab Rechtsverlusthinweis. R. 136 Abs. 1 EPÜ (Wiedereinsetzung): 2 Monate ab Wegfall des Hindernisses (S. 1) + absolut 12 Monate ab Fristablauf (S. 2). R. 51 Abs. 2 EPÜ (Jahresgebühren-Nachfrist): 6 Monate ab Fälligkeit. Art. 63 Abs. 1 EPÜ (Laufzeit): 20 Jahre ab Anmeldetag. Option A verwechselt die 2- und 6-Monats-Fristen zwischen Weiterbehandlung und Jahresgebühren. Option C nennt falsche Höchstfristen (24 Monate, 25 Jahre). Option D setzt die Jahresgebühren-Nachfrist auf 3 Monate — diese Frist existiert im EPÜ nicht.",
    sourceIds: ["r135epc", "r136epc", "r51epc", "art63epc"],
    sources: [
      { id: "r135epc", label: "R. 135 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r135.html" },
      { id: "r136epc", label: "R. 136 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r136.html" },
      { id: "r51epc", label: "R. 51 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/r51.html" },
      { id: "art63epc", label: "Art. 63 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a63.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Fristwert",
    skillTags: ["eqe", "fristen", "fristwert", "pruefungsschema"],
    reviewStatus: "beta-ready",
  },
];
